import { useState, useEffect } from 'react';
import { Coins, Wallet, Clock, Trophy } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import AnimatedBadge from './AnimatedBadge';
import { BADGE_TIERS } from '../lib/badges';

// Flat perks (same for every rank today). The authoritative values live
// server-side in src/lib/coins.ts (REDEEM_CAP_PCT, EXPIRY_MONTHS); these
// labels are display copy and must be kept in sync with them.
const REDEEM_CAP_LABEL = 'Up to 20%';
const COIN_VALIDITY_LABEL = '12 months';

/** 0.015 → "1.5%", 0.03 → "3%" (strips a trailing ".0"). */
const pct = (r: number) => `${(r * 100).toFixed(1).replace(/\.0$/, '')}%`;

interface Row {
  icon: typeof Coins;
  label: string;
  /** Per-rank value. */
  value: (t: (typeof BADGE_TIERS)[number]) => string;
  /** True for the row that actually differs by rank (visually emphasised). */
  highlight?: boolean;
}

const ROWS: Row[] = [
  { icon: Coins, label: 'Cashback on every order', value: (t) => pct(t.rate), highlight: true },
  { icon: Wallet, label: 'Redeem at checkout', value: () => REDEEM_CAP_LABEL },
  { icon: Clock, label: 'Coins valid for', value: () => COIN_VALIDITY_LABEL },
];

/**
 * "Compare ranks" — a NeuPass-style benefits grid for the five loyalty tiers.
 * Columns are the ranks (badge art + name + order range); rows are the perks.
 * Only the cashback rate differs by rank today; the other rows are flat and
 * de-emphasised so the real differentiator stands out. The viewer's current
 * rank column is spotlighted. Scrolls horizontally on narrow screens with a
 * sticky benefit-label column.
 */
export default function RankComparison({ currentSlug = null, id }: { currentSlug?: string | null; id?: string }) {
  const { isDark } = useTheme();
  // Shrink header badges on phones so all five ranks fit without horizontal scroll.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const badgeSize = narrow ? 28 : 40;
  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  // Background used by the sticky first column so scrolling cells pass under it.
  const stickyBg = isDark ? 'bg-gray-900' : 'bg-white';
  const border = isDark ? 'border-gray-800' : 'border-gray-200';

  return (
    <section id={id} className={`${id ? 'scroll-mt-24 ' : ''}rounded-2xl border p-4 sm:p-6 ${card}`}>
      <div className="flex items-center gap-2 mb-2">
        <Trophy size={18} className="text-amber-500" />
        <h2 className="text-xl font-bold">Ranks</h2>
      </div>
      <p className={`text-sm ${sub}`}>
        Every <strong>delivered order</strong> moves you up the ladder — from <strong>Aagman</strong> to the top tier{' '}
        <strong>Asur</strong>. The higher your rank, the more you earn back on every order; here’s what each tier unlocks.
      </p>

      {/* Fits all five ranks on mobile (no scroll); keeps the desktop min-width. */}
      <div className="mt-5 -mx-4 px-4 sm:-mx-6 sm:px-6 overflow-x-auto">
        <table className="w-full sm:min-w-[620px] border-separate border-spacing-0 text-xs sm:text-sm">
          <thead>
            <tr>
              <th
                className={`sticky left-0 z-10 ${stickyBg} text-left align-bottom pb-3 pr-2 sm:pb-4 sm:pr-4 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider ${sub}`}
              >
                Benefits
              </th>
              {BADGE_TIERS.map((t) => {
                const isCur = t.slug === currentSlug;
                const range = t.max === Infinity ? `${t.min}+` : `${t.min}–${t.max}`;
                return (
                  <th
                    key={t.slug}
                    className={`pb-3 px-1 sm:pb-4 sm:px-2 text-center align-top border-b ${border} ${
                      isCur ? 'bg-emerald-500/10 rounded-t-xl' : ''
                    }`}
                  >
                    <span
                      className={`block mb-1 text-[8px] sm:text-[9px] font-bold uppercase tracking-wide leading-tight ${
                        isCur ? 'text-emerald-500' : 'text-transparent'
                      }`}
                    >
                      You’re here
                    </span>
                    <span className="flex justify-center">
                      <AnimatedBadge src={t.src} glow={t.glow} size={badgeSize} variant="subtle" alt={`${t.name} badge`} />
                    </span>
                    <span className="block mt-1 font-bold text-[11px] sm:text-sm leading-tight">{t.name}</span>
                    <span className={`block text-[9px] sm:text-[10px] font-medium leading-tight ${sub}`}>{range} orders</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row, ri) => {
              const last = ri === ROWS.length - 1;
              return (
                <tr key={row.label}>
                  <th
                    scope="row"
                    className={`sticky left-0 z-10 ${stickyBg} text-left font-medium py-2.5 pr-2 sm:py-3 sm:pr-4 ${
                      last ? '' : `border-b ${border}`
                    }`}
                  >
                    <span className="flex items-center gap-1.5 sm:gap-2">
                      <row.icon size={14} className={`shrink-0 ${row.highlight ? 'text-emerald-500' : sub}`} />
                      <span className="leading-tight">{row.label}</span>
                    </span>
                  </th>
                  {BADGE_TIERS.map((t) => {
                    const isCur = t.slug === currentSlug;
                    return (
                      <td
                        key={t.slug}
                        className={`text-center py-2.5 px-1 sm:py-3 sm:px-2 ${last ? '' : `border-b ${border}`} ${
                          isCur ? 'bg-emerald-500/10' : ''
                        } ${
                          row.highlight
                            ? 'font-extrabold text-emerald-500 text-sm sm:text-base'
                            : `font-medium ${sub}`
                        }`}
                      >
                        {row.value(t)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className={`mt-4 text-xs ${sub}`}>
        Cashback is credited as ZS Coins when your order is delivered. Each coin is worth ₹1; redeem for up to 20% of a
        future order. Coins expire 12 months after they’re earned.
      </p>
    </section>
  );
}
